/**
 * Rehype plugin: lay consecutive stand-alone file links out as one set of cards.
 *
 * A paragraph whose only content is an artefact link renders as a file card (ADR-22 decision 4).
 * Written one after another — which is what a model does when it hands over three files without
 * a bullet list — each card took a full row, so the same three files looked completely different
 * depending on whether the model used bullets or blank lines.
 *
 * Grouping is a *layout* decision about adjacency, which is exactly what the renderer knows and
 * the model should not have to think about. A run of one is left alone: a single hand-over reads
 * better full width, and wrapping it would put a grid around one cell.
 */

import { artifactCard } from './ArtifactCard.css';
import { soleArtifactLink } from './ArtifactRef';
import type { HastNode } from './MdTable';

/** @desc A run shorter than this stays as it is — one card is not a grid. */
const MIN_RUN = 2;

const isCardParagraph = (node: HastNode): boolean =>
  node.type === 'element' && node.tagName === 'p' && soleArtifactLink(node) !== null;

/**
 * The newline between two block elements arrives as its own text node, so a run of paragraphs is
 * never actually adjacent in the tree. Held rather than dropped: between grouped cards it is
 * formatting and goes, anywhere else it is content and stays.
 */
const isBlank = (node: HastNode): boolean =>
  node.type === 'text' && (node.value ?? '').trim() === '';

export function rehypeGroupArtifactCards() {
  return (tree: HastNode): void => {
    const walk = (node: HastNode): void => {
      const children = node.children;
      if (!children) return;

      const grouped: HastNode[] = [];
      let run: HastNode[] = [];
      let pending: HastNode[] = [];

      const flush = () => {
        if (run.length >= MIN_RUN) {
          grouped.push({
            type: 'element',
            tagName: 'div',
            properties: { className: [artifactCard.list] },
            children: run,
          });
        } else {
          grouped.push(...run);
        }
        run = [];
      };

      for (const child of children) {
        if (isCardParagraph(child)) {
          // Whitespace held since the last card was between two of them: formatting, not content.
          pending = [];
          run.push(child);
          continue;
        }
        if (run.length > 0 && isBlank(child)) {
          pending.push(child);
          continue;
        }
        flush();
        grouped.push(...pending, child);
        pending = [];
        walk(child);
      }
      flush();
      grouped.push(...pending);

      node.children = grouped;
    };

    walk(tree);
  };
}
